import type { ReactNode } from 'react';
import { cn } from '@discover-lib/utils';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  type AccordionTriggerProps,
} from '@discover-ui/accordion';
import Button from '@discover-ui/button';
import Separator from '@discover-ui/separator';
import ChevronDownIcon from '@discover-ui/icons/chevron-down.svg';
import ChevronUpIcon from '@discover-ui/icons/chevron-up.svg';

type FilterAccordionItemProps = {
  value: string;
  title: string;
  selectedCount: number;
  onClear: () => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  hasMore: boolean;
  triggerProps?: AccordionTriggerProps;
  children: ReactNode;
};

export default function FilterAccordionItem({
  value,
  title,
  selectedCount,
  onClear,
  expanded,
  onToggleExpanded,
  hasMore,
  triggerProps,
  children,
}: FilterAccordionItemProps) {
  return (
    <AccordionItem value={value} className="group gap-1">
      <div
        className={cn('grid', {
          'grid-cols-[auto,1fr]': selectedCount === 0,
          'grid-cols-[auto,auto,1fr]': selectedCount > 0,
        })}>
        <AccordionTrigger {...triggerProps} className={cn('pr-2', triggerProps?.className)}>
          <span className="text-base leading-none lg:font-bold">{title}</span>
        </AccordionTrigger>
        {selectedCount > 0 ? (
          <Button
            kind="navigate"
            className="p-0 px-2 pt-0.5 text-[11px] text-gray"
            onClick={onClear}>
            CLEAR&nbsp;({selectedCount})
          </Button>
        ) : null}
        <AccordionTrigger
          {...triggerProps}
          aria-label={title}
          className={cn('justify-end', triggerProps?.className)}>
          <ChevronDownIcon className="mx-1 size-4 shrink-0 text-gray transition-transform duration-200" />
        </AccordionTrigger>
      </div>
      <AccordionContent>{children}</AccordionContent>
      <div className="relative flex flex-col items-center justify-center">
        <Separator className="my-3 lg:my-5" />
        {hasMore && (
          <Button
            round
            width="square"
            kind="outline"
            className="invisible absolute p-2.5 text-gray opacity-0 outline-border group-data-[state=open]:visible group-data-[state=open]:opacity-100"
            onClick={onToggleExpanded}
            aria-expanded={expanded}
            aria-label={expanded ? 'Show fewer options' : 'Show more options'}
            title={expanded ? 'Show fewer options' : 'Show more options'}>
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </Button>
        )}
      </div>
    </AccordionItem>
  );
}
